import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { updatePreferences, sendCode } from '../Services/services';
import { setLoading, clearNeedsSurvey } from '../store';
import { EMAIL_SUBSYSTEM_USER_MESSAGE } from '../constants/authMessages';
import { useBranding } from '../Context/BrandingContext';

function Preferences({ onComplete, onBack, onCancel, variant = 'page' }) {
  const dispatch = useDispatch();
  const { email, surveyData, loading } = useSelector((state) => state.auth);
  const { branding } = useBranding();
  
  const [formData, setFormData] = useState({
    companyName: '',
    name: '',
    phone: '',
    jobTitle: '',
    roles: [],
    seniority: '',
    businessSegments: [],
    supportRep: false,
  });
  const [errors, setErrors] = useState({});

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleMultiSelect = (name, value) => {
    setFormData((prev) => {
      const current = prev[name] || [];
      if (current.includes(value)) {
        return { ...prev, [name]: current.filter((v) => v !== value) };
      }
      return { ...prev, [name]: [...current, value] };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    const newErrors = {};
    if (!formData.companyName.trim()) newErrors.companyName = 'Company name is required';
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.phone.trim()) newErrors.phone = 'Phone is required';
    if (!formData.jobTitle.trim()) newErrors.jobTitle = 'Job title is required';
    if (formData.roles.length === 0) newErrors.roles = 'Please select at least one role';
    if (!formData.seniority) newErrors.seniority = 'Please select your seniority level';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    dispatch(setLoading(true));

    try {
      const preferencesData = {
        email,
        name: formData.name,
        companyName: formData.companyName,
        phone: formData.phone,
        jobTitle: formData.jobTitle,
        role: formData.roles.join(';'),
        seniority: formData.seniority,
        businessSegment: formData.businessSegments.join(';'),
        supportRep: formData.supportRep,
        url: window.location.href,
      };

      const response = await updatePreferences(preferencesData);
      
      if (response.data?.status !== 1) {
        throw new Error(response.data?.message || 'Failed to save preferences');
      }

      // After preferences saved, send code to proceed to verification.
      // If the email subsystem is down, advance to Verify anyway — the notice
      // will be shown there, not here.
      let emailError = null;
      try {
        await sendCode(email);
      } catch (sendErr) {
        emailError = EMAIL_SUBSYSTEM_USER_MESSAGE;
      }

      dispatch(clearNeedsSurvey());
      onComplete(emailError);
    } catch (err) {
      // Stay on Preferences only for genuine preferences-save failures.
      const msg = err.response?.data?.message || err.message || 'Failed to save preferences';
      dispatch({ type: 'auth/setError', payload: msg });
    } finally {
      dispatch(setLoading(false));
    }
  };

  const roles = surveyData?.roles || ['Sales', 'Operations', 'Accounting', 'IT', 'Executive'];
  const seniorities = surveyData?.seniorities || ['Entry Level', 'Mid Level', 'Senior', 'Manager', 'Director', 'VP', 'C-Level'];
  const businessSegments = surveyData?.businessSegments || ['Commercial', 'Defense', 'General Aviation', 'Rotorcraft'];

  // Dynamic styles based on branding
  const dynamicStyles = {
    companyName: {
      ...styles.companyName,
      color: branding.primaryColor,
    },
    title: {
      ...styles.title,
      color: branding.primaryColor,
    },
    button: {
      ...styles.button,
      backgroundColor: branding.primaryColor,
    },
  };

  const content = (
    <>
      <div style={styles.modalHeader}>
        <h2 style={styles.modalTitle}>Welcome</h2>
        <p style={styles.modalSubtitle}>
          Please complete this short survey to help us create your account and provide relevant information.
        </p>
      </div>

      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={styles.field}>
            <label style={styles.label}>Email</label>
            <input type="text" value={email || ''} disabled style={variant === 'modal' ? styles.modalInputDisabled : styles.inputDisabled} />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Company *</label>
            <input
              type="text"
              name="companyName"
              value={formData.companyName}
              onChange={handleInputChange}
              style={styles.input}
              maxLength={255}
            />
            {errors.companyName && <span style={styles.error}>{errors.companyName}</span>}
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Your Name *</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              style={styles.input}
              maxLength={255}
            />
            {errors.name && <span style={styles.error}>{errors.name}</span>}
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Job Title *</label>
            <input
              type="text"
              name="jobTitle"
              value={formData.jobTitle}
              onChange={handleInputChange}
              style={styles.input}
              maxLength={255}
            />
            {errors.jobTitle && <span style={styles.error}>{errors.jobTitle}</span>}
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Phone *</label>
            <input
              type="text"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              style={styles.input}
              maxLength={255}
            />
            {errors.phone && <span style={styles.error}>{errors.phone}</span>}
          </div>

          <div style={styles.checkboxField}>
            <input
              type="checkbox"
              name="supportRep"
              checked={formData.supportRep}
              onChange={handleInputChange}
              id="supportRep"
            />
            <label htmlFor="supportRep" style={styles.checkboxLabel}>
              I am the AvSight Support Contact
            </label>
          </div>

          <div style={styles.fieldGroup}>
            <div style={styles.fieldColumn}>
              <label style={styles.label}>Role *</label>
              {roles.map((role, index) => (
                <div key={index} style={styles.checkboxField}>
                  <input
                    type="checkbox"
                    checked={formData.roles.includes(role)}
                    onChange={() => handleMultiSelect('roles', role)}
                    id={`role-${index}`}
                  />
                  <label htmlFor={`role-${index}`} style={styles.checkboxLabel}>{role}</label>
                </div>
              ))}
              {errors.roles && <span style={styles.error}>{errors.roles}</span>}
            </div>

            <div style={styles.fieldColumn}>
              <label style={styles.label}>Seniority *</label>
              {seniorities.map((seniority, index) => (
                <div key={index} style={styles.checkboxField}>
                  <input
                    type="radio"
                    name="seniority"
                    value={seniority}
                    checked={formData.seniority === seniority}
                    onChange={handleInputChange}
                    id={`seniority-${index}`}
                  />
                  <label htmlFor={`seniority-${index}`} style={styles.checkboxLabel}>{seniority}</label>
                </div>
              ))}
              {errors.seniority && <span style={styles.error}>{errors.seniority}</span>}
            </div>

            <div style={styles.fieldColumn}>
              <label style={styles.label}>Business Segments</label>
              {businessSegments.map((segment, index) => (
                <div key={index} style={styles.checkboxField}>
                  <input
                    type="checkbox"
                    checked={formData.businessSegments.includes(segment)}
                    onChange={() => handleMultiSelect('businessSegments', segment)}
                    id={`segment-${index}`}
                  />
                  <label htmlFor={`segment-${index}`} style={styles.checkboxLabel}>{segment}</label>
                </div>
              ))}
            </div>
          </div>

          <div style={styles.buttonGroup}>
            <button type="button" onClick={onBack} style={variant === 'modal' ? styles.modalSecondaryButton : styles.secondaryButton} disabled={loading}>
              Back
            </button>
            <button type="submit" style={variant === 'modal' ? { ...styles.modalPrimaryButton, backgroundColor: branding.primaryColor } : dynamicStyles.button} disabled={loading}>
              {loading ? 'Saving...' : 'Continue'}
            </button>
          </div>

          {variant === 'modal' && onCancel && (
            <button type="button" onClick={onCancel} style={styles.modalTertiaryButton} disabled={loading}>
              Cancel
            </button>
          )}
        </form>
    </>
  );

  if (variant === 'modal') {
    return <div>{content}</div>;
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.header}>
          <p style={dynamicStyles.companyName}>{branding.companyName}</p>
          <h2 style={dynamicStyles.title}>Welcome to Customer Portal</h2>
          <p style={styles.subtitle}>
            Please complete this short survey to help us create your account and provide relevant information.
          </p>
        </div>
        <form onSubmit={handleSubmit}>
          <div style={styles.field}>
            <label style={styles.label}>Email</label>
            <input type="text" value={email || ''} disabled style={styles.inputDisabled} />
          </div>
          <div style={styles.field}>
            <label style={styles.label}>Company *</label>
            <input
              type="text"
              name="companyName"
              value={formData.companyName}
              onChange={handleInputChange}
              style={styles.input}
              maxLength={255}
            />
            {errors.companyName && <span style={styles.error}>{errors.companyName}</span>}
          </div>
          <div style={styles.field}>
            <label style={styles.label}>Your Name *</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              style={styles.input}
              maxLength={255}
            />
            {errors.name && <span style={styles.error}>{errors.name}</span>}
          </div>
          <div style={styles.field}>
            <label style={styles.label}>Job Title *</label>
            <input
              type="text"
              name="jobTitle"
              value={formData.jobTitle}
              onChange={handleInputChange}
              style={styles.input}
              maxLength={255}
            />
            {errors.jobTitle && <span style={styles.error}>{errors.jobTitle}</span>}
          </div>
          <div style={styles.field}>
            <label style={styles.label}>Phone *</label>
            <input
              type="text"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              style={styles.input}
              maxLength={255}
            />
            {errors.phone && <span style={styles.error}>{errors.phone}</span>}
          </div>
          <div style={styles.checkboxField}>
            <input
              type="checkbox"
              name="supportRep"
              checked={formData.supportRep}
              onChange={handleInputChange}
              id="supportRep"
            />
            <label htmlFor="supportRep" style={styles.checkboxLabel}>
              I am the AvSight Support Contact
            </label>
          </div>
          <div style={styles.fieldGroup}>
            <div style={styles.fieldColumn}>
              <label style={styles.label}>Role *</label>
              {roles.map((role, index) => (
                <div key={index} style={styles.checkboxField}>
                  <input
                    type="checkbox"
                    checked={formData.roles.includes(role)}
                    onChange={() => handleMultiSelect('roles', role)}
                    id={`role-${index}`}
                  />
                  <label htmlFor={`role-${index}`} style={styles.checkboxLabel}>{role}</label>
                </div>
              ))}
              {errors.roles && <span style={styles.error}>{errors.roles}</span>}
            </div>
            <div style={styles.fieldColumn}>
              <label style={styles.label}>Seniority *</label>
              {seniorities.map((seniority, index) => (
                <div key={index} style={styles.checkboxField}>
                  <input
                    type="radio"
                    name="seniority"
                    value={seniority}
                    checked={formData.seniority === seniority}
                    onChange={handleInputChange}
                    id={`seniority-${index}`}
                  />
                  <label htmlFor={`seniority-${index}`} style={styles.checkboxLabel}>{seniority}</label>
                </div>
              ))}
              {errors.seniority && <span style={styles.error}>{errors.seniority}</span>}
            </div>
            <div style={styles.fieldColumn}>
              <label style={styles.label}>Business Segments</label>
              {businessSegments.map((segment, index) => (
                <div key={index} style={styles.checkboxField}>
                  <input
                    type="checkbox"
                    checked={formData.businessSegments.includes(segment)}
                    onChange={() => handleMultiSelect('businessSegments', segment)}
                    id={`segment-${index}`}
                  />
                  <label htmlFor={`segment-${index}`} style={styles.checkboxLabel}>{segment}</label>
                </div>
              ))}
            </div>
          </div>
          <div style={styles.buttonGroup}>
            <button type="button" onClick={onBack} style={styles.secondaryButton} disabled={loading}>
              Back
            </button>
            <button type="submit" style={dynamicStyles.button} disabled={loading}>
              {loading ? 'Saving...' : 'Continue'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const styles = {
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    minHeight: '100vh',
    backgroundColor: '#f5f5f5',
    padding: '40px 20px',
  },
  card: {
    backgroundColor: 'white',
    padding: '40px',
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    width: '100%',
    maxWidth: '700px',
  },
  header: {
    textAlign: 'center',
    marginBottom: '24px',
  },
  companyName: {
    margin: '0 0 4px 0',
    fontSize: '14px',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '1px',
  },
  title: {
    margin: '0 0 8px 0',
    fontSize: '24px',
    fontWeight: '700',
    color: '#0066cc',
  },
  subtitle: {
    margin: '0',
    color: '#666',
    fontSize: '14px',
  },
  field: {
    marginBottom: '16px',
  },
  label: {
    display: 'block',
    marginBottom: '6px',
    fontWeight: '500',
    color: '#333',
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '14px',
    boxSizing: 'border-box',
  },
  inputDisabled: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '14px',
    boxSizing: 'border-box',
    backgroundColor: '#f5f5f5',
    color: '#666',
  },
  fieldGroup: {
    display: 'flex',
    gap: '24px',
    marginTop: '24px',
    marginBottom: '24px',
  },
  fieldColumn: {
    flex: 1,
  },
  checkboxField: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '8px',
    gap: '8px',
  },
  checkboxLabel: {
    fontSize: '14px',
    color: '#333',
    cursor: 'pointer',
  },
  error: {
    color: '#d32f2f',
    fontSize: '12px',
    marginTop: '4px',
    display: 'block',
  },
  buttonGroup: {
    display: 'flex',
    gap: '12px',
    marginTop: '24px',
  },
  button: {
    flex: 1,
    padding: '12px',
    backgroundColor: '#0066cc',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  secondaryButton: {
    flex: 1,
    padding: '12px',
    backgroundColor: '#6c757d',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
  },

  // Modal variant
  modalHeader: {
    marginBottom: 16,
  },
  modalTitle: {
    margin: '0 0 6px 0',
    fontSize: 22,
    fontWeight: 600,
    color: 'var(--foreground)',
  },
  modalSubtitle: {
    margin: 0,
    fontSize: 14,
    color: 'var(--muted-foreground)',
  },
  modalInputDisabled: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid var(--border)',
    borderRadius: 'var(--radius)',
    fontSize: '14px',
    boxSizing: 'border-box',
    backgroundColor: 'var(--muted)',
    color: 'var(--muted-foreground)',
  },
  modalPrimaryButton: {
    flex: 1,
    padding: '10px 12px',
    border: 'none',
    borderRadius: 'var(--radius)',
    color: 'var(--primary-foreground)',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
  },
  modalSecondaryButton: {
    flex: 1,
    padding: '10px 12px',
    border: '1px solid var(--border)',
    borderRadius: 'var(--radius)',
    backgroundColor: 'var(--secondary)',
    color: 'var(--secondary-foreground)',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
  },
  modalTertiaryButton: {
    width: '100%',
    padding: '10px 12px',
    border: 'none',
    borderRadius: 'var(--radius)',
    backgroundColor: 'transparent',
    color: 'var(--muted-foreground)',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
  },
};

export default Preferences;
